import {HTMLAttributes} from 'react';

import styled from 'styled-components';

import {flipOnLTR} from '~/shared/theme/utils';
import TriangleIcon from '~/assets/images/svgIconsAsText/triangle.svg?react';

import {body16Normal} from '../theme/typography';

const Root = styled.div<{hasError: boolean}>`
  position: relative;
  display: flex;
  align-items: center;
  height: 48px;
  min-height: 48px;
  border-radius: 6px;
  background-color: ${({theme}) => theme.colors.surface};
  border: 1px solid ${({theme, hasError}) => (hasError ? theme.colors.error : theme.colors.gray500)};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: ${({theme, hasError}) => (hasError ? theme.colors.error : theme.colors.surfacePrimaryAction)};
    box-shadow: 0 0 0 2px
      ${({theme, hasError}) => `${hasError ? theme.colors.error : theme.colors.surfacePrimaryAction}33`};
  }
`;

const InnerSelect = styled.select<{hasValue: boolean}>`
  appearance: none;
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  cursor: pointer;
  background-color: transparent;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  ${flipOnLTR`
    padding-right: 14px;
    padding-left: 40px;
    text-align: right;
  `};

  &&& {
    ${body16Normal}
    color: ${({theme, hasValue}) => (hasValue ? theme.colors.secondary : theme.colors.gray500)};
  }

  option {
    color: ${({theme}) => theme.colors.secondary};
  }
`;

const TriangleWrapper = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  bottom: 0;
  width: 32px;
  pointer-events: none;
  ${flipOnLTR`
    left: 4px;
  `}
`;

const Triangle = styled(TriangleIcon)`
  width: 10px;
  height: 6px;
`;

export interface ISelect {
  hasError: boolean;
  options: {value: string; text: string}[];
  value: any;
  placeholder: string;
  name: string;
  onChange: (value: string) => void;
  required: boolean;
  withBorder?: boolean;
  rest?: HTMLAttributes<HTMLSelectElement>[];
}

const Select = ({options, value, placeholder, name, onChange, hasError, required, withBorder: _withBorder, ...rest}: ISelect) => {
  const label = `${required ? '* ' : ''}${placeholder || name}`;
  const hasValue = !!value;

  return (
    <Root hasError={hasError}>
      <InnerSelect
        hasValue={hasValue}
        value={value || ''}
        required={required}
        aria-label={placeholder || name}
        onChange={e => onChange(e.target.value)}
        {...rest}
      >
        <option value="" disabled hidden>
          {label}
        </option>
        {options?.map(({value: optionValue, text: optionLabel}: {value: string; text: string}) => {
          return (
            <option key={optionValue} value={optionValue}>
              {optionLabel}
            </option>
          );
        })}
      </InnerSelect>
      <TriangleWrapper>
        <Triangle />
      </TriangleWrapper>
    </Root>
  );
};

export default Select;
